import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import { BASE_URL } from "../../../../components/config/config";

const RemarkModal = ({
  open,
  handleClose,
  ticketId,
  username,
  initialRemarks,
  processedName,
  onRemarkAdded,
}) => {
  const effectiveUser = username || "Client";

  const [remarks, setRemarks] = useState(
    initialRemarks
      ? [{ text: initialRemarks, name: processedName || effectiveUser }]
      : []
  );
  const [newRemark, setNewRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRemarks(
      initialRemarks
        ? [{ text: initialRemarks, name: processedName || effectiveUser }]
        : []
    );
    setNewRemark("");
  }, [initialRemarks, processedName, ticketId]);

  const saveRemark = async () => {
    if (!newRemark.trim()) return;
    try {
      setSaving(true);
      await axios.post(`${BASE_URL}admin/onboarding-team-remarks`, {
        onboarding_team_remarks: newRemark,
        onboarding_team_name: effectiveUser,
        ticket_obj_id: ticketId,
      });

      const newEntry = { text: newRemark.trim(), name: effectiveUser };
      setRemarks((prev) => [...prev, newEntry]);
      onRemarkAdded(ticketId, newEntry.text, newEntry.name);
      setNewRemark("");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(90deg, #0d9488, #14b8a6)",
          color: "white",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        Remarks
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: "#f9fafb",
        }}
      >
        {/* Input */}
        <TextField
          label="Write a remark"
          multiline
          minRows={3}
          variant="outlined"
          fullWidth
          value={newRemark}
          onChange={(e) => setNewRemark(e.target.value)}
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0d9488",
              "&:hover": { backgroundColor: "#0f766e" },
              borderRadius: "20px",
              textTransform: "none",
              px: 3,
            }}
            disabled={!newRemark.trim() || saving}
            onClick={saveRemark}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>

        {/* List */}
        <Box
          sx={{
            maxHeight: 280,
            overflowY: "auto",
            marginTop: 2,
          }}
        >
          {remarks.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
              mt={2}
            >
              No remarks yet.
            </Typography>
          ) : (
            remarks.map((r, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  borderRadius: 2,
                  backgroundColor: "white",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#0d9488",
                    color: "white",
                    fontSize: 14,
                    width: 36,
                    height: 36,
                  }}
                >
                  {r.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box flexGrow={1}>
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    {r.text}
                  </Typography>
                  <Chip
                    label={r.name}
                    size="small"
                    sx={{
                      mt: 1,
                      backgroundColor: "#e0f2f1",
                      color: "#0d9488",
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Paper>
            ))
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb" }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            color: "#0d9488",
            fontWeight: 500,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemarkModal;
